/*
  GA4 «Items clicked» (#141668). The Google & YouTube app sends every other
  GA4 event (page views, list views, cart, checkout, purchase) but never
  select_item, because Shopify has no "product clicked in a list" event. This
  script adds only that event:

  - A product link or quick-add clicked inside a [data-ls-list] list
    (marked by snippets/ls-list-attrs.liquid) sends select_item with the list.
  - It is sent into the app's own GA4 user and session (the _ga cookies), so
    GA4 credits the list to the app's later add_to_cart and purchase.
  - The app's item ids are per variant (shopify_ZZ_<product>_<variant>) and the
    size is chosen only on the product page, so the click lists every variant
    of the product: a jersey click counts once per size in «Items clicked».
  - Names are French (Storefront API), so FR and EN visits share one list row.

  A click that opens the product page is sent from that next page: a request
  started while a page unloads gets lost. Nothing is sent without analytics
  consent or without the app's _ga cookie.
*/
(function () {
  var GA_ID = 'G-8X90V1816J';
  // While true, only a browser with localStorage ls_ga4_test = '1' sends (preview test).
  var TEST_ONLY = true;
  var DEBUG = false; // true marks hits for GA4 DebugView
  var COLLECT = 'https://region1.google-analytics.com/g/collect';
  var API = '/api/2025-07/graphql.json';
  var PENDING_KEY = 'ls_ga4_select';

  function stored(key) {
    try {
      return localStorage.getItem(key);
    } catch (e) {
      return null;
    }
  }

  function enabled() {
    return !TEST_ONLY || stored('ls_ga4_test') === '1';
  }

  function analyticsAllowed() {
    var c = window.Cookiebot && window.Cookiebot.consent;
    if (c && typeof c.statistics === 'boolean') return c.statistics;
    var cp = window.Shopify && window.Shopify.customerPrivacy;
    if (cp && typeof cp.analyticsProcessingAllowed === 'function') return cp.analyticsProcessingAllowed();
    return false;
  }

  function cookie(name) {
    var m = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/[.$?*|{}()[\]\\/+^]/g, '\\$&') + '=([^;]*)'));
    return m ? decodeURIComponent(m[1]) : '';
  }

  // The app's GA4 ids: client from _ga (GA1.1.<cid>), session from
  // _ga_<container> (GS2.1.s<sid>$o<count>$… or the older GS1.1.<sid>.<count>.…).
  function gaIds() {
    var ga = cookie('_ga').split('.');
    var ids = { cid: ga.length >= 4 ? ga.slice(-2).join('.') : '', sid: '', sct: '' };
    var s = cookie('_ga_' + GA_ID.slice(2));
    var m = s.match(/^GS2\.\d\.s(\d+)\$o(\d+)/) || s.match(/^GS1\.\d\.(\d+)\.(\d+)/);
    if (m) {
      ids.sid = m[1];
      ids.sct = m[2];
    }
    return ids;
  }

  function storefront(query, variables) {
    return fetch(API, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ query: query, variables: variables }),
    })
      .then(function (r) {
        return r.json();
      })
      .then(function (j) {
        return j.data || {};
      })
      .catch(function () {
        return {};
      });
  }

  function listName(sel) {
    if (!sel.collection_id) return Promise.resolve(sel.list_name || sel.list_id);
    return storefront('query ($id: ID!) @inContext(language: FR) { collection(id: $id) { title } }', {
      id: 'gid://shopify/Collection/' + sel.collection_id,
    }).then(function (d) {
      var title = (d.collection && d.collection.title) || '';
      return [sel.list_name, title].filter(Boolean).join(' · ') || sel.list_id;
    });
  }

  function productFr(handle) {
    return storefront(
      'query ($h: String!) @inContext(language: FR) { product(handle: $h) { id title vendor productType variants(first: 100) { nodes { id title price { amount currencyCode } } } } }',
      { h: handle }
    ).then(function (d) {
      return d.product || null;
    });
  }

  function num(gid) {
    return String(gid || '').split('/').pop();
  }

  // One GA4 item in the /g/collect encoding (id…~nm…~…).
  function pr(it) {
    return [
      ['id', it.id],
      ['nm', it.name],
      ['va', it.variant],
      ['br', it.brand],
      ['ca', it.category],
      ['pr', it.price],
      ['li', it.listId],
      ['ln', it.listName],
      ['lp', it.index],
    ]
      .filter(function (kv) {
        return kv[1] !== undefined && kv[1] !== null && kv[1] !== '';
      })
      .map(function (kv) {
        return kv[0] + String(kv[1]).replace(/~/g, '-');
      })
      .join('~');
  }

  function sendSelect(sel) {
    if (!enabled() || !analyticsAllowed()) return;
    var ids = gaIds();
    // Without the app's client id the click would start a new GA4 user.
    if (!ids.cid) return;
    Promise.all([listName(sel), productFr(sel.handle)]).then(function (res) {
      var name = res[0];
      var p = res[1];
      var variants = (p && p.variants.nodes) || [];
      if (!variants.length) return;
      var q = new URLSearchParams({
        v: '2',
        tid: GA_ID,
        cid: ids.cid,
        en: 'select_item',
        dl: location.href,
        dt: document.title,
        cu: (variants[0].price && variants[0].price.currencyCode) || 'CHF',
        'ep.item_list_id': sel.list_id,
        'ep.item_list_name': name,
      });
      if (ids.sid) {
        q.set('sid', ids.sid);
        q.set('sct', ids.sct || '1');
        q.set('seg', '1');
      }
      if (document.referrer) q.set('dr', document.referrer);
      if (DEBUG) q.set('_dbg', '1');
      variants.forEach(function (v, i) {
        q.set(
          'pr' + (i + 1),
          pr({
            id: 'shopify_ZZ_' + num(p.id) + '_' + num(v.id),
            name: p.title,
            variant: variants.length > 1 ? v.title : '',
            brand: p.vendor,
            category: p.productType,
            price: v.price ? Number(v.price.amount) : '',
            listId: sel.list_id,
            listName: name,
            index: sel.index >= 0 ? sel.index : '',
          })
        );
      });
      try {
        navigator.sendBeacon(COLLECT + '?' + q.toString());
      } catch (e) {}
    });
  }

  function handleOf(link) {
    var m = (link.getAttribute('href') || '').match(/\/products\/([^/?#]+)/);
    return m ? decodeURIComponent(m[1]) : null;
  }

  function handlesIn(el) {
    var out = [];
    el.querySelectorAll('a[href*="/products/"]').forEach(function (a) {
      var h = handleOf(a);
      if (h && out.indexOf(h) === -1) out.push(h);
    });
    return out;
  }

  function selection(listEl, handle) {
    return {
      list_id: listEl.getAttribute('data-ls-list'),
      list_name: listEl.getAttribute('data-ls-list-name') || '',
      collection_id: listEl.getAttribute('data-ls-list-collection') || '',
      handle: handle,
      index: handlesIn(listEl).indexOf(handle),
    };
  }

  // The card around a quick-add button: the nearest ancestor that links to one product.
  function cardHandle(el, listEl) {
    for (var n = el; n && n !== listEl; n = n.parentElement) {
      var hs = handlesIn(n);
      if (hs.length === 1) return hs[0];
      if (hs.length > 1) return null;
    }
    return null;
  }

  document.addEventListener(
    'click',
    function (ev) {
      var link = ev.target.closest && ev.target.closest('a[href*="/products/"]');
      var listEl = link && link.closest('[data-ls-list]');
      if (!listEl) return;
      var handle = handleOf(link);
      if (!handle) return;
      var sel = selection(listEl, handle);
      var newTab = ev.ctrlKey || ev.metaKey || ev.shiftKey || ev.button !== 0 || link.target === '_blank';
      if (newTab || ev.defaultPrevented) {
        sendSelect(sel);
        return;
      }
      sel.t = Date.now();
      try {
        localStorage.setItem(PENDING_KEY, JSON.stringify(sel));
      } catch (e) {
        sendSelect(sel);
      }
    },
    true
  );

  // Quick add from a card: the page stays, so send at once.
  document.addEventListener(
    'submit',
    function (ev) {
      var form = ev.target;
      if (!form.matches || !form.matches('form[action*="/cart/add"]')) return;
      var listEl = form.closest('[data-ls-list]');
      if (!listEl) return;
      var handle = cardHandle(form, listEl);
      if (handle) sendSelect(selection(listEl, handle));
    },
    true
  );

  function flushPending() {
    var sel = null;
    try {
      sel = JSON.parse(localStorage.getItem(PENDING_KEY));
      localStorage.removeItem(PENDING_KEY);
    } catch (e) {}
    if (!sel || Date.now() - sel.t > 60000) return;
    delete sel.t;
    sendSelect(sel);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', flushPending);
  } else {
    flushPending();
  }
})();
